import { FieldArray, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Divider, FormLabel } from '@mui/material';
import { IFormValuesPersonalInfo, IFormValuesVoluntarySelfIdentification } from '../types';
import {
  AboriginalGroup,
  AboriginalGroupLookups,
  DisabilityType,
  DisabilityTypeLookups,
  MinorityGroup,
  MinorityGroupLookups,
} from '../constants';
import { ChipMultiselectField, DatePickerField, SelectField, TextField } from '../../../../../components/FormFields';
import CustomLabel from '../../../../../components/CustomLabel';
import theme from '../../../../../app/layout/theme';
import { DATE_FORMAT } from '../../../../../app/utils/dateTimeHelpers';
import useLocale from '../../../../../app/hooks/useLocale';
import {
  useAnswerTypes,
  useCountriesQuery,
  useEducationLevelsQuery,
  useGenderOptionsQuery,
  useProvincesQuery,
} from '../queries';
import { getVoluntarySelfIdentificationItem } from '../utils';
import { ClientPublicService } from '../../../../../app/api/ClientPublicService';

interface Props {
  name: string;
  isLocked?: boolean;
  isForSpouse?: boolean;
}

const VoluntarySelfIdentificationValues = ({ name, isLocked, isForSpouse = false }: Props) => {
  const { t } = useLocale();
  const { values, setFieldValue } = useFormikContext<IFormValuesPersonalInfo>();

  const [aboriginalSelectionValues, setAboriginalSelectionValues] = useState<AboriginalGroup[][]>([[]]);
  const [disabilitySelectionValues, setDisabilitySelectionValues] = useState<DisabilityType[][]>([[]]);
  const [minortySelectionValues, setMinortySelectionValues] = useState<MinorityGroup[][]>([[]]);

  const isOtherDisabilityEnabled = useMemo(
    () =>
      disabilitySelectionValues.map((item) => {
        return item.includes(DisabilityType.OtherDisabilityType);
      }),
    [disabilitySelectionValues]
  );

  const isOtherMinorityEnabled = useMemo(
    () =>
      minortySelectionValues.map((item) => {
        return item.includes(MinorityGroup.OtherVisibleMinorityGroup);
      }),
    [minortySelectionValues]
  );

  const newSelfIdentification = useMemo(() => getVoluntarySelfIdentificationItem(undefined, isForSpouse), []);

  const handleAboriginalChange = useCallback(
    (index: number, selectedValues: AboriginalGroup[]) => {
      const existingList = (values as any)?.[name];

      if (!existingList || existingList.length === 0) return;

      const updatedEntry = {
        ...existingList[index],
        isNorthAmericanIndianFirstNation: selectedValues.includes(AboriginalGroup.NorthAmericanIndianFirstNation),
        isMétis: selectedValues.includes(AboriginalGroup.Métis),
        isInuit: selectedValues.includes(AboriginalGroup.Inuit),
      };

      const newList = [...existingList];
      newList[index] = updatedEntry;
      setFieldValue(name, newList);

      const newAboriginalSelectionValues = [...aboriginalSelectionValues];
      newAboriginalSelectionValues[index] = selectedValues;
      setAboriginalSelectionValues(newAboriginalSelectionValues);
    },
    [values, name, aboriginalSelectionValues, setFieldValue]
  );

  const handleDisabilityChange = useCallback(
    (index: number, selectedValues: DisabilityType[]) => {
      const existingList = (values as any)?.[name];

      if (!existingList || existingList.length === 0) return;

      const updatedEntry = {
        ...existingList[index],
        hasDisabilityCoordinationOrDexterity: selectedValues.includes(DisabilityType.CoordinationOrDexterity),
        hasDisabilityMobility: selectedValues.includes(DisabilityType.Mobility),
        hasDisabilityBlindOrVisualImpairment: selectedValues.includes(DisabilityType.BlindOrVisualImpairment),
        hasDisabilityDeafOrHardOfHearing: selectedValues.includes(DisabilityType.DeafOrHardOfHearing),
        hasDisabilitySpeechImpairment: selectedValues.includes(DisabilityType.SpeechImpairment),
        hasDisabilityOther: selectedValues.includes(DisabilityType.OtherDisabilityType),
      };

      const newList = [...existingList];
      newList[index] = updatedEntry;
      setFieldValue(name, newList);

      const newDisabilitySelectionValues = [...disabilitySelectionValues];
      newDisabilitySelectionValues[index] = selectedValues;
      setDisabilitySelectionValues(newDisabilitySelectionValues);
    },
    [values, name, disabilitySelectionValues, setFieldValue]
  );

  const handleMinorityChange = useCallback(
    (index: number, selectedValues: MinorityGroup[]) => {
      const existingList = (values as any)?.[name];

      if (!existingList || existingList.length === 0) return;

      const updatedEntry = {
        ...existingList[index],
        isBlack: selectedValues.includes(MinorityGroup.Black),
        isChinese: selectedValues.includes(MinorityGroup.Chinese),
        isFilipino: selectedValues.includes(MinorityGroup.Filipino),
        isJapanese: selectedValues.includes(MinorityGroup.Japanese),
        isKorean: selectedValues.includes(MinorityGroup.Korean),
        isSouthAsianEastIndian: selectedValues.includes(MinorityGroup.SouthAsianEastIndian),
        isSoutheastAsian: selectedValues.includes(MinorityGroup.SoutheastAsian),
        isNonWhiteWestAsianNorthAfricanOrArab: selectedValues.includes(
          MinorityGroup.NonWhiteWestAsianNorthAfricanOrArab
        ),
        isNonWhiteLatinAmerican: selectedValues.includes(MinorityGroup.NonWhiteLatinAmerican),
        isPersonOfMixedOrigin: selectedValues.includes(MinorityGroup.PersonOfMixedOrigin),
        isOtherVisibleMinorityGroup: selectedValues.includes(MinorityGroup.OtherVisibleMinorityGroup),
      };

      const newList = [...existingList];
      newList[index] = updatedEntry;
      setFieldValue(name, newList);

      const newMinoritySelectionValues = [...minortySelectionValues];
      newMinoritySelectionValues[index] = selectedValues;
      setMinortySelectionValues(newMinoritySelectionValues);
    },
    [values, name, minortySelectionValues, setFieldValue]
  );

  const { data: genderOptions } = useGenderOptionsQuery() || {};
  const { data: countryOptions } = useCountriesQuery() || {};
  const { data: answerTypes } = useAnswerTypes() || {};
  const { data: educationLevels } = useEducationLevelsQuery() || {};

  const canadaCountryId = useMemo(
    () => countryOptions?.find((country) => country.name === 'Canada')?.id,
    [countryOptions]
  );

  const { data: provinceOptions, isLoading: provincesLoading } = useProvincesQuery(canadaCountryId) || {};

  // Initial value setters
  useEffect(() => {
    const arr = (values as any)?.[name];
    if (!arr || arr.length === 0) {
      setFieldValue(name, [newSelfIdentification]);
      return;
    }

    // Aboriginal
    setAboriginalSelectionValues(
      arr.map((item: IFormValuesVoluntarySelfIdentification) => {
        const selected: AboriginalGroup[] = [];
        if (item?.isNorthAmericanIndianFirstNation) selected.push(AboriginalGroup.NorthAmericanIndianFirstNation);
        if (item?.isInuit) selected.push(AboriginalGroup.Inuit);
        if (item?.isMétis) selected.push(AboriginalGroup.Métis);
        return selected;
      })
    );
    // Disability
    setDisabilitySelectionValues(
      arr.map((item: IFormValuesVoluntarySelfIdentification) => {
        const selected: DisabilityType[] = [];
        if (item?.hasDisabilityCoordinationOrDexterity) selected.push(DisabilityType.CoordinationOrDexterity);
        if (item?.hasDisabilityMobility) selected.push(DisabilityType.Mobility);
        if (item?.hasDisabilityBlindOrVisualImpairment) selected.push(DisabilityType.BlindOrVisualImpairment);
        if (item?.hasDisabilityDeafOrHardOfHearing) selected.push(DisabilityType.DeafOrHardOfHearing);
        if (item?.hasDisabilitySpeechImpairment) selected.push(DisabilityType.SpeechImpairment);
        if (item?.hasDisabilityOther) selected.push(DisabilityType.OtherDisabilityType);
        return selected;
      })
    );
    // Minority
    setMinortySelectionValues(
      arr.map((item: IFormValuesVoluntarySelfIdentification) => {
        const selected: MinorityGroup[] = [];
        if (item?.isBlack) selected.push(MinorityGroup.Black);
        if (item?.isChinese) selected.push(MinorityGroup.Chinese);
        if (item?.isFilipino) selected.push(MinorityGroup.Filipino);
        if (item?.isJapanese) selected.push(MinorityGroup.Japanese);
        if (item?.isKorean) selected.push(MinorityGroup.Korean);
        if (item?.isSouthAsianEastIndian) selected.push(MinorityGroup.SouthAsianEastIndian);
        if (item?.isSoutheastAsian) selected.push(MinorityGroup.SoutheastAsian);
        if (item?.isNonWhiteWestAsianNorthAfricanOrArab)
          selected.push(MinorityGroup.NonWhiteWestAsianNorthAfricanOrArab);
        if (item?.isNonWhiteLatinAmerican) selected.push(MinorityGroup.NonWhiteLatinAmerican);
        if (item?.isPersonOfMixedOrigin) selected.push(MinorityGroup.PersonOfMixedOrigin);
        if (item?.isOtherVisibleMinorityGroup) selected.push(MinorityGroup.OtherVisibleMinorityGroup);
        return selected;
      })
    );
  }, [values, name, newSelfIdentification, setFieldValue]);

  return (
    <FieldArray name={name}>
      {() => (
        <>
          <Divider sx={{ mt: 2, mb: 1 }} />
          {(values as any)?.[name]?.map((item: any, index: number) => {
            return (
              <Stack key={`${index}-si`}>
                <SelectField
                  label={t.WHAT_IS_YOUR_GENDER}
                  name={`${name}.${index}.genderId`}
                  options={genderOptions}
                  disabled={isLocked}
                />
                <SelectField
                  label={t.HIGHEST_LEVEL_OF_EDUCATION_COMPLETED}
                  name={`${name}.${index}.educationLevelId`}
                  options={educationLevels}
                  disabled={isLocked}
                />
                <SelectField
                  label={t.ARE_YOU_AN_ABORIGINAL_PERSON}
                  name={`${name}.${index}.isAboriginal`}
                  options={answerTypes}
                  disabled={isLocked}
                  useEnumValue={true}
                />
                {Number(item.isAboriginal ?? -1) === ClientPublicService.AnswerEnum.Yes && (
                  <ChipMultiselectField
                    label={t.IF_YOU_WISH_TO_PROVIDE_FURTHER_DETAILS_PLEASE_SPECIFY_THE_GROUP_TO_WHICH_YOU_BELONG}
                    options={AboriginalGroupLookups}
                    name={`${name}.${index}.testing1`}
                    value={aboriginalSelectionValues[index] ?? []}
                    disabled={isLocked}
                    onChange={(selected: any) => handleAboriginalChange(index, selected?.target?.value)}
                    useEnumValues={true}
                  />
                )}
                <SelectField
                  label={t.ARE_YOU_A_PERSON_WITH_DISABILITY}
                  name={`${name}.${index}.hasDisability`}
                  options={answerTypes}
                  disabled={isLocked}
                  useEnumValue={true}
                />
                {Number(item.hasDisability ?? -1) === ClientPublicService.AnswerEnum.Yes && (
                  <>
                    <ChipMultiselectField
                      label={t.IF_YOU_WISH_TO_PROVIDE_FURTHER_DETAILS_PLEASE_SELECT_THE_BOXES_THAT_APPLY_TO_YOU}
                      options={DisabilityTypeLookups}
                      name={`${name}.${index}.testing2`}
                      value={disabilitySelectionValues[index] ?? []}
                      disabled={isLocked}
                      onChange={(selected: any) => handleDisabilityChange(index, selected?.target?.value)}
                      useEnumValues={true}
                    />
                    {isOtherDisabilityEnabled[index] && (
                      <TextField
                        name={`${name}.${index}.disabilityOtherDescription`}
                        label={t.OTHER_DISABILITY_PLEASE_SPECIFY}
                        multiline
                        rows={4}
                        maxLength={5000}
                        disabled={isLocked}
                        required={isOtherDisabilityEnabled[index]}
                      />
                    )}
                  </>
                )}
                <SelectField
                  label={t.ARE_YOU_A_MEMBER_OF_A_VISIBLE_MINORITY_GROUP}
                  name={`${name}.${index}.isMemberOfMinorityGroup`}
                  options={answerTypes}
                  disabled={isLocked}
                  useEnumValue={true}
                />
                {Number(item.isMemberOfMinorityGroup ?? -1) === ClientPublicService.AnswerEnum.Yes && (
                  <>
                    <ChipMultiselectField
                      label={
                        t.IF_YOU_WISH_TO_PROVIDE_FURTHER_DETAILS_PLEASE_SELECT_THE_BOXES_THAT_BEST_DESCRIBES_YOUR_ORIGIN
                      }
                      options={MinorityGroupLookups}
                      name={`${name}.${index}.testing3`}
                      value={minortySelectionValues[index] ?? []}
                      disabled={isLocked}
                      onChange={(selected: any) => handleMinorityChange(index, selected?.target?.value)}
                      useEnumValues={true}
                    />
                    {isOtherMinorityEnabled[index] && (
                      <TextField
                        name={`${name}.${index}.otherVisibleMinorityGroupDescription`}
                        label={t.OTHER_VISIBLE_MINORITY_GROUP_SPECIFY}
                        multiline
                        rows={4}
                        maxLength={5000}
                        disabled={isLocked || !isOtherMinorityEnabled[index]}
                        required={isOtherMinorityEnabled[index]}
                      />
                    )}
                  </>
                )}
                <br />
                <FormLabel sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: 14 }}>
                  <CustomLabel label={t.THE_CONSENT_IS_PROVIDED} required />
                </FormLabel>
                <DatePickerField
                  name={`${name}.${index}.consentDate`}
                  label={t.DATE}
                  disableFuture
                  format={DATE_FORMAT}
                  disabled={isLocked}
                  required
                />
                <TextField
                  name={`${name}.${index}.consentCity`}
                  label={t.CITY}
                  inputProps={{ maxLength: 100 }}
                  disabled={isLocked}
                  required
                />
                <SelectField
                  label={t.PROVINCE}
                  name={`${name}.${index}.consentProvinceId`}
                  options={provinceOptions}
                  disabled={isLocked}
                  loading={!countryOptions || provincesLoading}
                  required
                />
              </Stack>
            );
          })}
        </>
      )}
    </FieldArray>
  );
};

export default VoluntarySelfIdentificationValues;
